import React, { useCallback, useEffect, useState } from "react";
import { X, Loader2, Search, Check } from "lucide-react";

import { searchComparables } from "../api/prospectingReportsApi";

const KINDS = [
  { key: "sold", label: "Sold" },
  { key: "for_sale", label: "For sale" },
  { key: "leased", label: "Leased" },
];

const money = (v) => (v ? `$${Math.round(Number(v)).toLocaleString("en-AU")}` : "");

/** A comparable row in the shape the proposal's comparables block stores. */
export const toBlockRow = (c, kind) => ({
  address: c.street_address || c.full_address || "",
  suburb: c.suburb || "",
  beds: c.bedrooms != null ? String(c.bedrooms) : "",
  baths: c.bathrooms != null ? String(c.bathrooms) : "",
  cars: c.car_spaces != null ? String(c.car_spaces) : "",
  price_label: c.price ? money(c.price) : (c.price_label || ""),
  sold_date: c.sold_date || "",
  image_url: c.image_url || "",
  kind: c.kind || kind,
});

/**
 * Pick comparables for a proposal with the same search the CMA uses (CG QA,
 * 26 Oct). Searches around the proposal's suburb; the agent ticks the rows to
 * add and they are saved onto the comparables section.
 */
const ComparablePickerModal = ({
  proposal, onClose, onAdd, kinds = KINDS.map((k) => k.key),
  title = "Pick comparables", subtitle = "The same comparable search the CMA uses. Tick the sales to add.",
  actionLabel = "Add to proposal",
}) => {
  const [kind, setKind] = useState(kinds[0] || "sold");
  const [suburb, setSuburb] = useState(proposal?.suburb || "");
  const [beds, setBeds] = useState("");
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState({});
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const run = useCallback(() => {
    if (!suburb.trim()) { setRows([]); return; }
    setLoading(true);
    setFailed(false);
    const params = { suburb: suburb.trim(), state: proposal?.state || undefined, limit: 30 };
    if (beds) { params.beds_min = beds; params.beds_max = beds; }
    searchComparables({ kind, ...params })
      .then(({ data }) => setRows(Array.isArray(data) ? data : (data?.results || [])))
      // The first search for a suburb can be slow while market data loads.
      .catch(() => { setRows([]); setFailed(true); })
      .finally(() => setLoading(false));
  }, [kind, suburb, beds, proposal]);

  useEffect(() => { run(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const rowKey = (c) => `${c.source}-${c.source_id || c.full_address}`;
  const toggle = (c) => setPicked((p) => {
    const k = rowKey(c);
    const next = { ...p };
    if (next[k]) delete next[k]; else next[k] = c;
    return next;
  });
  const chosen = Object.values(picked);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-5 border-b border-gray-100 flex flex-wrap items-end gap-3">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {KINDS.filter((k) => kinds.includes(k.key)).map((k) => (
              <button key={k.key} onClick={() => setKind(k.key)}
                className={`px-3 py-2 text-sm font-semibold ${kind === k.key ? "bg-[#0f8d9b] text-white" : "text-gray-600"}`}>{k.label}</button>
            ))}
          </div>
          <label className="text-xs font-semibold text-gray-500">Suburb
            <input value={suburb} onChange={(e) => setSuburb(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
              className="block mt-1 w-44 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </label>
          <label className="text-xs font-semibold text-gray-500">Beds
            <select value={beds} onChange={(e) => setBeds(e.target.value)} className="block mt-1 rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <option value="">Any</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button onClick={run} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700">
            <Search className="w-4 h-4" /> Search
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {loading ? <div className="flex justify-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
            failed ? (
              <div className="p-4 text-sm text-gray-500">
                The search didn't finish (market data can take a moment to load the first time).{" "}
                <button onClick={run} className="font-semibold text-[#0f8d9b]">Try again</button>
              </div>
            ) : rows.length === 0 ? <p className="text-sm text-gray-400 p-4">No comparables found. Try another suburb or kind.</p> : (
              <ul className="divide-y divide-gray-100">
                {rows.map((c) => {
                  const on = !!picked[rowKey(c)];
                  return (
                    <li key={rowKey(c)} onClick={() => toggle(c)}
                      className={`flex items-center gap-3 p-2 cursor-pointer rounded-lg ${on ? "bg-[#eef8f9]" : "hover:bg-gray-50"}`}>
                      <span className={`w-5 h-5 rounded border flex items-center justify-center ${on ? "bg-[#0f8d9b] border-[#0f8d9b]" : "border-gray-300"}`}>
                        {on && <Check className="w-3.5 h-3.5 text-white" />}
                      </span>
                      {c.image_url ? <img src={c.image_url} alt="" className="w-14 h-10 object-cover rounded" /> : <span className="w-14 h-10 rounded bg-gray-100" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{c.full_address}</p>
                        <p className="text-xs text-gray-500">
                          {[c.bedrooms != null && `${c.bedrooms} bed`, c.bathrooms != null && `${c.bathrooms} bath`, c.car_spaces != null && `${c.car_spaces} car`].filter(Boolean).join(" · ")}
                          {c.sold_date ? ` · sold ${c.sold_date}` : ""}{c.is_collings_listing ? " · Collings" : ""}
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-gray-800">{c.price ? money(c.price) : (c.price_label || "")}</span>
                    </li>
                  );
                })}
              </ul>
            )
          )}
        </div>
        <div className="flex items-center justify-between p-4 border-t border-gray-100">
          <span className="text-sm text-gray-500">{chosen.length} selected</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700">Cancel</button>
            <button disabled={!chosen.length} onClick={() => onAdd(chosen.map((c) => toBlockRow(c, kind)), chosen)}
              className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50 bg-[#0f8d9b]">{actionLabel}{chosen.length ? ` (${chosen.length})` : ""}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparablePickerModal;
